import { useState, useEffect } from "react";
import {
  Show,
  SignIn,
  UserButton,
  useAuth,
} from "@clerk/react";
import { DealProvider } from "@/context/DealContext";
import { ChatLayout } from "@/components/chat/ChatLayout";
import { setTokenGetter, signalAuthReady } from "@/api/client";
import "@/styles/global.css";

/** Bridges Clerk's getToken into the API client layer.
 *
 * Two-layer gate:
 * 1. Children don't mount until we've confirmed getToken() returns a real JWT.
 * 2. apiFetch() itself awaits signalAuthReady(), so even stray early calls block.
 *
 * This prevents 401s on redirect-back from Stripe / Clerk sign-in where
 * isLoaded is true but the session token isn't available yet.
 */
function ClerkTokenBridge({ children }: { children: React.ReactNode }) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [ready, setReady] = useState(false);

  // Keep the token getter wired synchronously (always current reference)
  setTokenGetter(getToken);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;

    let cancelled = false;
    let attempt = 0;

    const tryGetToken = () => {
      attempt++;
      getToken().then((token) => {
        if (cancelled) return;
        if (token) {
          signalAuthReady();
          setReady(true);
        } else if (attempt < 5) {
          // Token not ready yet (Clerk session still loading) — retry
          setTimeout(tryGetToken, 500);
        }
      }).catch(() => {
        if (cancelled) return;
        if (attempt < 5) {
          setTimeout(tryGetToken, 500);
        }
      });
    };

    tryGetToken();

    return () => { cancelled = true; };
  }, [isLoaded, isSignedIn, getToken]);

  if (!ready) return null;

  return <>{children}</>;
}

export default function App() {
  return (
    <>
      <Show when="signed-out">
        <div className="auth-page">
          <div className="auth-container">
            <div className="auth-header">
              <h1>Hoosier Intel</h1>
              <p className="subtitle">Indiana CRE Intelligence</p>
            </div>
            <SignIn routing="hash" />
          </div>
        </div>
      </Show>
      <Show when="signed-in">
        <ClerkTokenBridge>
          <DealProvider>
            <ChatLayout />
          </DealProvider>
        </ClerkTokenBridge>
      </Show>
    </>
  );
}
